import { z } from 'zod';
import { DELISTING_ACTIONS, NEWS_CATEGORIES, NEWS_SOURCE_KEYS, VETO_REASONS } from '../enums';
import { jsonObjectSchema, uuidSchema } from './common';
import { newsSettingsSchema } from './settings';

/** News sources, news items and symbol vetoes (API_DOCS.md — News Sources, Risk). */

export const newsItemDtoSchema = z.object({
  id: uuidSchema,
  provider: z.string(),
  category: z.enum(NEWS_CATEGORIES),
  title: z.string(),
  url: z.string().nullable(),
  symbols: z.array(z.string()),
  publishedAt: z.string().datetime(),
  duplicateOf: uuidSchema.nullable(),
});
export type NewsItemDto = z.infer<typeof newsItemDtoSchema>;

export const newsQuerySchema = z.object({
  symbol: z.string().optional(),
  category: z.enum(NEWS_CATEGORIES).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type NewsQuery = z.infer<typeof newsQuerySchema>;

const sourceStatusSchema = z.object({
  enabled: z.boolean(),
  pollIntervalSec: z.number().int(),
  healthy: z.boolean().nullable(),
  lastPollAt: z.string().datetime().nullable(),
  itemsToday: z.number().int().nonnegative(),
});

export const newsSourceDtoSchema = z.discriminatedUnion('key', [
  sourceStatusSchema.extend({
    key: z.literal('rss'),
    feeds: z.array(
      z.object({
        name: z.string(),
        url: z.string().url(),
        enabled: z.boolean(),
        healthy: z.boolean().nullable(),
      }),
    ),
  }),
  sourceStatusSchema.extend({
    key: z.literal('binance_announcements'),
    delistingAction: z.enum(DELISTING_ACTIONS),
  }),
]);
export type NewsSourceDto = z.infer<typeof newsSourceDtoSchema>;

export const newsSourceKeySchema = z.enum(NEWS_SOURCE_KEYS);

export const newsSourceUpdateSchema = z
  .object({
    enabled: z.boolean().optional(),
    params: jsonObjectSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'at least one field must be provided');
export type NewsSourceUpdate = z.infer<typeof newsSourceUpdateSchema>;

export const newsSourceTestResponseSchema = z.object({
  ok: z.boolean(),
  detail: z.string().optional(),
  items: z.array(
    z.object({
      title: z.string(),
      category: z.enum(NEWS_CATEGORIES),
      symbols: z.array(z.string()),
      publishedAt: z.string().datetime(),
    }),
  ),
});
export type NewsSourceTestResponse = z.infer<typeof newsSourceTestResponseSchema>;

/** GET/PUT /news/symbol-map — the SymbolResolver dictionary (ARCHITECTURE.md §9.8). */
export const symbolMapSchema = newsSettingsSchema.pick({
  symbolAliases: true,
  ambiguousTickers: true,
});
export type SymbolMap = z.infer<typeof symbolMapSchema>;

export const symbolVetoDtoSchema = z.object({
  symbol: z.string(),
  reason: z.enum(VETO_REASONS),
  newsItemId: uuidSchema,
  newsTitle: z.string(),
  at: z.string().datetime(),
});
export type SymbolVetoDto = z.infer<typeof symbolVetoDtoSchema>;
